/**
 * Gate for the /platform/* route subtree.
 *
 * Two rejection paths:
 *   1. No JWT in storage → /login.
 *   2. JWT but `is_platform_staff=false` → /dashboard (customer surface).
 *
 * A third path — JWT + platform-staff but `mfa_enabled=false` → inline
 * MFA-enrolment notice — was removed 2026-09-08 (backlog §9.3, "don't
 * force 2FA/passkey enrollment anywhere"). The backend no longer
 * enforces this either (`apps.platform.permissions.IsPlatformStaff`),
 * so there is nothing left for this guard to pre-empt.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { isAuthenticated } from "@/lib/auth";
import { stopImpersonation } from "@/lib/impersonation";
import { usePlatformMe } from "@/lib/platform";
import { tokenStore } from "@/lib/tokenStore";
import { Spinner } from "./Spinner";

export function PlatformRouteGuard() {
  const { t } = useTranslation();
  const location = useLocation();
  // The platform-staff console and a partner impersonation are mutually
  // exclusive — the server rejects an impersonation token on /platform/*
  // endpoints (403), yet /platform/me tolerates it and renders a healthy-
  // looking admin shell while every data panel silently 403s. If we ENTER the
  // platform subtree with an impersonation overlay ALREADY active (the admin
  // navigated to /platform instead of clicking "exit impersonation"), drop it
  // so the whole subtree runs on the real admin token.
  //
  // Scoped to "stale at first mount" only: STARTING an impersonation from
  // /platform/partners sets the overlay AFTER this guard is already mounted and
  // then navigates away to /partner — that must NOT be torn down mid-flight
  // (doing so strands the admin on /dashboard). `staleOverlay` is therefore
  // captured once, on the first render, and is false in the start-impersonation
  // case.
  const staleOverlay = useRef<boolean | null>(null);
  if (staleOverlay.current === null) {
    staleOverlay.current = !!tokenStore.getImpersonation();
  }
  useEffect(() => {
    if (staleOverlay.current && tokenStore.getImpersonation()) stopImpersonation();
  }, []);
  const me = usePlatformMe();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // While a stale overlay is being torn down (and queries re-fetch on the admin
  // token), hold on a spinner rather than flash the empty/403 console.
  if (staleOverlay.current && tokenStore.getImpersonation()) {
    return <Spinner fullscreen label={t("ui.platformRouteGuard.loading")} />;
  }
  if (me.isLoading) return <Spinner fullscreen label={t("ui.platformRouteGuard.loading")} />;
  if (me.isError || !me.data) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!me.data.is_platform_staff) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
